import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo, ArrowLink, CTAButton, Eyebrow, Marquee, Stat } from "@/components/site/primitives";
import { Reveal, Stagger, Item, MaskLines } from "@/components/site/motion";
import { Section, Pill } from "@/components/site/pageparts";
import { CopyInstall, VersionBadge } from "@/components/site/CopyInstall";
import {
  RetrievalFusionDiagram,
  MembraneDiagram,
  PersistenceMark,
} from "@/components/site/Diagrams";
import { POSTS } from "@/data/blog";
import { BackerStrip } from "@/components/site/Backers";

const THESES = [
  { n: "01", t: "Every AI session starts from zero.", d: "The model is brilliant and amnesiac. Context lives in a window that vanishes when the tab closes — working memory, not memory." },
  { n: "02", t: "Cloud memory means renting your own mind back.", d: "Your corpus is uploaded, embedded remotely, and metered by the token. The most personal thing you own becomes a vendor's leverage." },
  { n: "03", t: "Unverifiable recall is a liability, not a feature.", d: "An answer that cannot name its source is a rumor with good grammar. In medicine, law and finance, that is disqualifying." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ink grain">
      <div className="hairgrid pointer-events-none absolute inset-0 opacity-40" />
      <motion.img
        style={{ y }}
        src="/brand/mark-white.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-4 top-28 hidden w-[34vw] max-w-2xl opacity-[0.05] lg:block"
      />

      <div className="relative mx-auto max-w-[1400px] px-5 pb-14 pt-32 md:px-10 md:pb-20 md:pt-40">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <Eyebrow>DataFrontier · A research lab</Eyebrow>
        </motion.div>

        <h1 className="mt-7 font-serif text-[13vw] leading-[0.95] tracking-tightest text-bone sm:text-7xl md:text-8xl lg:text-[104px]">
          <MaskLines lines={["Intelligence", "is solved."]} delay={0.12} />
          <span className="mt-1 block">
            <MaskLines lines={["Memory isn\u2019t."]} lineClass="text-bone" delay={0.42} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.62, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-lg leading-[1.6] text-mist md:text-xl"
        >
          DataFrontier is a research lab building memory as infrastructure — sovereign, verifiable, and yours.
          Personal <span className="text-bone">(UPII)</span> to enterprise <span className="text-bone">(UEII)</span>, shipped as Smritium.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.78 }}
          className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
        >
          <CTAButton to="/smritium" variant="solid" data-testid="hero-explore-smritium">
            Explore Smritium <ArrowRight className="h-4 w-4" />
          </CTAButton>
          <div className="w-full max-w-sm sm:w-auto">
            <CopyInstall variant="inline" />
            <div className="mt-3 sm:hidden">
              <VersionBadge />
            </div>
          </div>
          <div className="hidden sm:block">
            <VersionBadge />
          </div>
        </motion.div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-10 gap-y-3 px-5 py-5 font-mono text-xs text-mist md:px-10">
          <span className="text-signal">0.958 Recall@10</span>
          <span className="opacity-30">/</span>
          <span>100% reproducible chunk hashes</span>
          <span className="opacity-30">/</span>
          <span>zero required cloud calls</span>
          <span className="opacity-30">/</span>
          <span>MIT · open-source core</span>
        </div>
      </div>
    </section>
  );
};

const ProgramCard = ({ tag, title, desc, metric, metricLabel, diagram, to }) => (
  <Item className="group flex flex-col border border-white/10 bg-white/[0.015] p-8 transition-colors duration-500 hover:border-white/20 md:p-10">
    <div className="flex items-center justify-between">
      <span className="font-mono text-xs uppercase tracking-[0.18em] text-saffron">{tag}</span>
      <ArrowUpRight className="h-5 w-5 text-mist transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-bone" />
    </div>
    <h3 className="mt-6 font-serif text-2xl tracking-tight text-bone md:text-3xl">{title}</h3>
    <p className="mt-4 text-[15px] leading-relaxed text-mist">{desc}</p>
    <div className="my-8 opacity-80">{diagram}</div>
    <div className="mt-auto flex items-end justify-between border-t border-white/10 pt-6">
      <div>
        <div className="font-serif text-3xl text-bone">{metric}</div>
        <div className="mt-1 text-xs text-mist">{metricLabel}</div>
      </div>
      <Link to={to} className="link-underline pb-0.5 text-sm text-bone group-hover:text-saffron">
        Read the work
      </Link>
    </div>
  </Item>
);

export default function Home() {
  const latest = POSTS.slice(0, 3);
  return (
    <div data-testid="home-page">
      <Seo title="Memory as infrastructure" description="DataFrontier is a research lab building memory as infrastructure — sovereign, verifiable, and yours." />
      <Hero />

      {/* The memory problem */}
      <Section>
        <Reveal>
          <Eyebrow>The memory problem</Eyebrow>
          <h2 className="mt-5 max-w-3xl text-balance font-serif text-3xl leading-[1.06] tracking-tighter text-bone md:text-5xl">
            Today's AI is brilliant and amnesiac. Three theses, in lab-note form.
          </h2>
        </Reveal>
        <Stagger className="mt-16 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {THESES.map((th) => (
            <Item key={th.n} className="bg-ink p-8 md:p-10">
              <div className="font-mono text-sm text-saffron">{th.n}</div>
              <h3 className="mt-6 font-serif text-xl leading-snug text-bone md:text-2xl">{th.t}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-mist">{th.d}</p>
            </Item>
          ))}
        </Stagger>
        <Reveal delay={0.1} className="mt-10">
          <ArrowLink to="/research/memory-problem">Read the manifesto</ArrowLink>
        </Reveal>
      </Section>

      {/* Research programs */}
      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>Research programs</Eyebrow>
          <h2 className="mt-5 max-w-3xl text-balance font-serif text-3xl leading-[1.06] tracking-tighter text-bone md:text-5xl">
            One substrate. Two kinds of sovereignty.
          </h2>
        </Reveal>
        <Stagger className="mt-16 grid gap-6 lg:grid-cols-2">
          <ProgramCard
            tag="UPII · Personal"
            title="Unified Personal Intelligence Infrastructure"
            desc="Local-first, sovereign memory for individuals. An open standard and reference implementation — the open-source core is live on PyPI."
            metric="pip install upii"
            metricLabel="MIT · Python 3.10–3.12"
            diagram={<PersistenceMark className="text-bone/50" />}
            to="/research/upii"
          />
          <ProgramCard
            tag="UEII · Enterprise"
            title="Unified Enterprise Intelligence Infrastructure"
            desc="The same guarantees at organizational scale: role-aware memory, the Smritium Membrane, and compliance by architecture — DPDP and GDPR."
            metric="Audit-ready"
            metricLabel="tamper-evident egress logs"
            diagram={<MembraneDiagram className="text-bone/50" />}
            to="/research/ueii"
          />
        </Stagger>
      </Section>

      {/* Marquee */}
      <div className="border-y border-white/10 bg-ink py-8 md:py-10">
        <Marquee items={["sovereign", "verifiable", "local-first", "cite-or-abstain", "deterministic", "yours"]} />
      </div>

      {/* Smritium flagship band */}
      <Section className="relative overflow-hidden">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Pill tone="saffron">Flagship</Pill>
              <h2 className="mt-6 font-serif text-4xl leading-[1.02] tracking-tighter text-bone md:text-6xl">Smritium</h2>
              <p className="mt-4 font-serif text-xl italic text-mist md:text-2xl">The sovereign memory layer for AI.</p>
              <p className="mt-8 max-w-md text-[15px] leading-relaxed text-mist">
                Most memory products upload your corpus to a provider and rent your recall back by the token. Smritium
                inverts the architecture — capture, embedding, retrieval and reasoning stay on your machine.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <CTAButton to="/smritium" variant="solid">
                  Explore Smritium <ArrowRight className="h-4 w-4" />
                </CTAButton>
                <CTAButton href="https://pypi.org/project/upii/" external variant="outline">
                  View on PyPI
                </CTAButton>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Stagger className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
              {[
                { t: "Local-first", d: "What never leaves cannot leak." },
                { t: "Cite-or-abstain", d: "No hallucinated sources." },
                { t: "Deterministic", d: "Reproducible by construction." },
              ].map((p) => (
                <Item key={p.t} className="bg-ink p-6">
                  <div className="font-serif text-lg text-bone">{p.t}</div>
                  <div className="mt-2 text-sm text-mist">{p.d}</div>
                </Item>
              ))}
            </Stagger>
            <Reveal delay={0.1}>
              <div className="mt-6 border border-white/10 bg-white/[0.015] p-8">
                <RetrievalFusionDiagram className="text-bone/70" />
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border border-signal/25 bg-signal/[0.04] px-5 py-4">
                <span className="text-sm text-bone">Open-source core live on PyPI — upii v0.7.0</span>
                <VersionBadge />
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* ASOC band — ~half the weight */}
      <Section className="border-t border-white/10">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Product · ASOC</Eyebrow>
            <h2 className="mt-5 max-w-xl text-balance font-serif text-2xl leading-tight tracking-tight text-bone md:text-4xl">
              An AI revenue operating system for outbound sales.
            </h2>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-mist">
              Memory-native pipeline intelligence, built on the same substrate: agent-driven research, persistent
              prospect context, and relationship history that compounds instead of resetting.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:justify-self-end">
            <CTAButton to="/products/asoc" variant="outline">
              Explore ASOC <ArrowUpRight className="h-4 w-4" />
            </CTAButton>
          </Reveal>
        </div>
      </Section>

      {/* Supported by */}
      <Section className="border-t border-white/10">
        <Reveal>
          <p className="text-center font-mono text-xs uppercase tracking-[0.18em] text-mist">Recognized & supported by</p>
        </Reveal>
        <Reveal delay={0.05}>
          <BackerStrip className="mt-10" />
        </Reveal>
      </Section>

      {/* Forward Deployed Engineering CTA */}
      <Section className="border-t border-white/10">
        <Reveal className="grid items-center gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Eyebrow>Forward Deployed Engineering</Eyebrow>
            <h2 className="mt-5 max-w-2xl text-balance font-serif text-2xl leading-tight tracking-tighter text-bone md:text-4xl">
              Have a novel problem? We embed an engineer to build the whole solution.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <CTAButton to="/company/forward-deployed" variant="outline">
              Deploy an engineer <ArrowUpRight className="h-4 w-4" />
            </CTAButton>
          </div>
        </Reveal>
      </Section>

      {/* From the lab */}
      <Section className="border-t border-white/10">
        <div className="flex items-end justify-between">
          <Reveal>
            <Eyebrow>From the lab</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">Recent notes</h2>
          </Reveal>
          <Reveal>
            <ArrowLink to="/blog" className="hidden md:inline-flex">All notes</ArrowLink>
          </Reveal>
        </div>
        <Stagger className="mt-14 divide-y divide-white/10 border-t border-white/10">
          {latest.map((p) => (
            <Item key={p.id}>
              <Link to={`/blog/${p.slug}`} className="group grid gap-4 py-8 md:grid-cols-12 md:gap-8">
                <div className="flex items-center gap-4 font-mono text-xs text-mist md:col-span-3">
                  <span className="text-saffron">{p.id}</span>
                  <span>{new Date(p.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                </div>
                <h3 className="font-serif text-xl leading-snug text-bone transition-colors duration-300 group-hover:text-saffron md:col-span-6 md:text-2xl">
                  {p.title}
                </h3>
                <p className="text-sm leading-relaxed text-mist md:col-span-3">{p.abstract.slice(0, 110)}…</p>
              </Link>
            </Item>
          ))}
        </Stagger>
      </Section>

      {/* Careers teaser */}
      <Section className="border-t border-white/10">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="text-balance font-serif text-3xl leading-tight tracking-tighter text-bone md:text-5xl">
            We hire people who think memory is the most important unsolved problem in AI.
          </h2>
          <div className="mt-10 flex justify-center">
            <CTAButton to="/company/careers" variant="outline">
              See open roles <ArrowUpRight className="h-4 w-4" />
            </CTAButton>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
